import { confirmPaymentIntent, createElement, getElement, loadAirwallex } from 'airwallex-payment-elements';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { CartAtom } from '~/services/cart';
import { OrderStatus } from '~/services/order';
import OrderAtom from '~/services/order/order.atom';
import { PaymentMethods } from '~/services/payment';
import storeConfigAtom from '~/services/store-config/store-config.atom';
import {
    createAirwallexIntent,
    createAirwallexIntentCustomer,
    placeholderAirwallex,
    placeholderCustomerAirwallex
} from './AirwallexServices';
import { PATH } from '~/constants/enum';

var count = 1;
const ConnectAirwallex = ({
    isPlaceholder,
    submitButton = (event: { processing: boolean; error: any; disabled: boolean }) => null
}: any) => {
    const { airwallex_mode } = useRecoilValue(storeConfigAtom.store);
    const [cart, setCart] = useRecoilState(CartAtom.cart);

    const setOrder = useSetRecoilState(OrderAtom.order);

    const [elementShow, setElementShow] = useState<boolean>(false); // Example: show element state
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false); // Example: show submission processing state
    const [errorMessage, setErrorMessage] = useState<any>(); // Example: set error state
    const [loadingText, setLoadingText] = useState<any>('Your order is processing, please wait...'); // Example: set error state
    // const [processing, setProcessing] = useState<any>(); // Example: set error state
    const [responsePayment, setResponsePayment] = useState<{
        client_secret: string;
        id: string;
    }>();
    const router = useRouter();
    const cartId = cart?.cartId as string;
    const email = cart?.email || '';
    const billingAddress = cart?.billing_address;
    const initRef = useRef<any>();

    const init = () => {
        if (typeof window === 'undefined') return;
        initRef.current = true;
        try {
            const token = localStorage.getItem('token');
            if (!!token) {
                createAirwallexIntentCustomer(
                    {
                        cart_id: cartId,
                        method: 'airwallex_payments_card',
                        return_url: process.env.NEXT_PUBLIC_URL || ''
                    },
                    localStorage.getItem('token') as string
                )
                    .then((response) => {
                        if (response.data?.client_secret && response.data?.id) {
                            setResponsePayment({
                                id: response.data?.id,
                                client_secret: response.data?.client_secret
                            });
                        } else {
                            toast.error(response?.data?.message || '');
                        }
                    })
                    .catch((error) => toast.error(error));
            } else {
                createAirwallexIntent({
                    cart_id: cartId,
                    method: 'airwallex_payments_card',
                    return_url: process.env.NEXT_PUBLIC_URL || '/'
                }).then((response) => {
                    if (response.data?.client_secret && response.data?.id) {
                        setResponsePayment({
                            id: response.data?.id,
                            client_secret: response.data?.client_secret
                        });
                    } else {
                        toast.error(response?.data?.message || '');
                    }
                });
            }
        } catch (error) {
            console.error(error);
            toast.error('Something went wrong. Please try again!');
        }
    };

    useEffect(() => {
        // STEP #2: Initialize Airwallex with the appropriate Airwallex environment and other configurations
        if (!!airwallex_mode) {
            loadAirwallex({
                env: airwallex_mode as any, // Can choose other production environments, 'staging | 'demo' | 'prod'
                origin: window.location.origin, // Setup your event target to receive the browser events message
                fonts: [
                    // Can customize the font for the payment elements
                    {
                        // src:
                        //   "https://checkout.airwallex.com/fonts/CircularXXWeb/CircularXXWeb-Regular.woff2",
                        family: 'Poppins, sans-serif;',
                        weight: 400
                    }
                ]
                // For more detailed documentation at https://github.com/airwallex/airwallex-payment-demo/tree/master/docs
            }).then(() => {
                // STEP #4: Create the card element
                const card: any = createElement('card');
                // STEP #5: Mount the card element to the empty container created previously
                card?.mount('card'); // This 'card' id MUST MATCH the id on your empty container created in Step 3
            });
        }
        // STEP #7: Add an event listener to ensure the element is mounted
        const onReady = (event: any) => {
            /**
             * ...Handle events on element ready
             */
            setIsSubmitting(false);
            setElementShow(true); // Example: sets show once mounted
            getElement('card')?.focus(); // Example: focuses on input field
        };

        // STEP #8: Add an event listener to respond to errors
        const onError = (event: any) => {
            /**
             * ... Handle events on error
             */
            const { error } = event.detail;
            setIsSubmitting(false);
            setErrorMessage(error.message);
            console.error('There was an error', error);
        };

        window.addEventListener('onReady', onReady);
        window.addEventListener('onError', onError);
        return () => {
            window.removeEventListener('onReady', onReady);
            window.removeEventListener('onError', onError);
        };
    }, [airwallex_mode]); // This effect should ONLY RUN ONCE as we do not want to reload Airwallex and remount the elements

    useEffect(() => {
        if (initRef.current) return;
        init();
    }, []);
    // useEffect(() => {
    //   setIsSubmitting(false)
    // }, [elementShow])

    const onTriggerConfirm = () => {
        // if (!isPlaceholder) {
        //     setCart((prevCart) => ({
        //         ...prevCart,
        //         statusPlaceOrder: 'failed'
        //     }));

        //     return null;
        // }
        if (!responsePayment?.client_secret) {
            return setCart((prevCart) => ({
                ...prevCart,
                statusPlaceOrder: 'failed'
            }));
        }
        setIsSubmitting(true);
        setErrorMessage(''); // Example: reset error message
        const card: any = getElement('card');
        try {
            return confirmPaymentIntent({
                element: card,
                id: responsePayment?.id,
                client_secret: responsePayment?.client_secret
            })
                .then(async (response: any) => {
                    // setIsSubmitting(false);
                    /**
                     * ...Handle confirm response
                     */
                    // window.alert(
                    //   `Payment Intent confirmation was successful: ${JSON.stringify(
                    //     response
                    //   )}`
                    // );
                    setCart((prevCart) => ({
                        ...prevCart,
                        selected_payment_method: {
                            title: 'Pay by Card',
                            code: PaymentMethods.AIRWALLEX
                        }
                    }));
                    const responseSendPlaceholder = !!localStorage.getItem('token')
                        ? await placeholderCustomerAirwallex(
                              {
                                  billingAddress: {
                                      city: billingAddress?.city || '',
                                      saveInAddressBook: null,
                                      company: '',
                                      telephone: billingAddress?.telephone,
                                      countryId: billingAddress?.country?.code || '',
                                      firstname: billingAddress?.firstname || '',
                                      lastname: billingAddress?.lastname || '',
                                      postcode: billingAddress?.postcode || '',
                                      region: billingAddress?.region_code || '',
                                      street: [billingAddress?.street, billingAddress?.street1].filter((i) => !!i)
                                  },
                                  paymentMethod: {
                                      method: 'airwallex_payments_card',
                                      po_number: null as any,
                                      additional_data: {
                                          amount: response?.amount,
                                          intent_id: response?.id,
                                          intent_status: response?.status
                                      }
                                  }
                              },
                              localStorage.getItem('token')
                          )
                        : await placeholderAirwallex({
                              cartId: cartId,
                              email: email,
                              paymentMethod: {
                                  method: 'airwallex_payments_card',
                                  po_number: '',
                                  additional_data: {
                                      amount: response?.amount,
                                      intent_id: response?.id,
                                      intent_status: response?.status
                                  }
                              }
                          });

                    if (!!responseSendPlaceholder) {
                        // Example: sets loading state
                        toast.success('Placeholder success');
                        setOrder({
                            ...cart,
                            order_number: responseSendPlaceholder?.data?.increment_id,
                            // status: OrderStatus.ORDER_PLACED,
                            selected_payment_method: {
                                title: 'Pay by card',
                                code: PaymentMethods.AIRWALLEX
                            },
                            referer: PATH.Checkout
                        });
                        router.push('/thank-you');
                        // setLoadingText('Redirecting...');
                        // dispatch(clearCartAction());
                        // createEmptyCart();
                    } else {
                        setCart((prevCart) => ({
                            ...prevCart,
                            statusPlaceOrder: 'failed'
                        }));

                        toast.error(responseSendPlaceholder?.message);
                    }
                })
                .catch((error: any) => {
                    /**
                     * ... Handle error response
                     */
                    setCart((prevCart) => ({
                        ...prevCart,
                        statusPlaceOrder: 'failed'
                    }));

                    const elementCartAirwallex = document.getElementById(
                        'selected_payment_method.' + PaymentMethods.AIRWALLEX
                    );
                    if (!!elementCartAirwallex) {
                        elementCartAirwallex.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center'
                        });
                    }
                    setErrorMessage(error.message ?? JSON.stringify(error)); // Example: set error message
                    console.error('There was an error', error);
                });
        } catch (error) {
            setErrorMessage(typeof error ? error : 'Something went wrong. Please payemnt again!');
            console.error('There was an error', error);
        }
    };

    useEffect(() => {
        if (cart?.statusPlaceOrder === 'processing') {
            onTriggerConfirm();
        }
    }, [cart?.statusPlaceOrder]);

    const inputStyle = {
        // Custom styling for the inputs, can be placed in css
        border: '1px solid',
        borderRadius: '5px',
        padding: '5px 10px',
        marginTop: '8px',
        minHeight: '32px',
        PointerEvent: isSubmitting ? 'none' : ''
    };

    return (
        <>
            <div
                className="container p-0"
                style={{
                    width: '100%'
                    // margin: '0 auto',
                }}
            >
                <div>
                    {/* Example below: show loading state */}
                    {!elementShow && <p>Loading...</p>}
                    {/* Example below: display response message block */}

                    <div
                        className="field-container"
                        style={{ display: elementShow ? 'block' : 'none' }} // Example: Custom styling to only show card when it is mounted
                    >
                        {/**
                         * STEP #3a: Add an empty container for the card element to be placed into
                         * - Ensure this is the only element in your document with this id,
                         *   otherwise the element may fail to mount.
                         */}
                        <div
                            id="card"
                            style={inputStyle} // Example: input styling can be moved to css
                        />
                        {/* STEP #3b: Add a submit button to trigger the payment request */}
                        {/* <div
                              style={{
                                  pointerEvents: isSubmitting ? 'none' : 'unset'
                              }}
                              onClick={onTriggerConfirm}
                          >
                              {submitButton({
                                  processing: isSubmitting,
                                  error: errorMessage,
                                  disabled: isSubmitting || !elementShow
                              })}
                          </div> */}
                        {/* <button
                                  onClick={onTriggerConfirm}
                                  disabled={isSubmitting} // Example: disables button when submitting to prevent resubmission
                              >
                              Submit
                          </button> */}
                    </div>
                    {errorMessage?.length > 0 && (
                        <p className="mt-2 mb-2 text-error" id="error">
                            {errorMessage}
                        </p>
                    )}
                </div>
            </div>
        </>
    );
};

export default ConnectAirwallex;
